import { ApolloQueryResult } from "@apollo/client/core";
import { ref, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { useLegacyAreaDialogNavigation } from "@/components/address-selector/composables/useLegacyAreaDialogNavigation";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import { getCityByPrefGroupedByStepA } from "@/common/graphql/cityByPref";
import { getPrefCitySectionByZipCode } from "@/common/graphql/zipCode";
import { getSectionByPrefCityGroupedByStepA } from "@/common/graphql/sectionByPrefCity";
import type { DeliveryAvailabilityData } from "../types";
import {
  DeliveryRequestUpdateInput,
  GetPrefByChainDocument,
  GetPrefByChainQuery,
  GetBlockByPrefCitySectionDocument,
  GetBlockByPrefCitySectionQuery,
  GetBlockByZipCodeDocument,
  GetBlockByZipCodeQuery,
  GetDeliveryAreaDocument,
  GetDeliveryAreaQuery,
  GetZipCodeByPrefCitySectionDocument,
  GetZipCodeByPrefCitySectionQuery,
  ListDeliveryAreaSectionUnassignedDocument,
  ListDeliveryAreaSectionUnassignedQuery,
  ListDeliveryAreaDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
} from "@/generated/graphql";

type KanaStepA = {
  __typename?: "Kana_Step_A" | undefined;
  line_a?: (string | null | undefined)[] | null | undefined;
  line_ka?: (string | null | undefined)[] | null | undefined;
  line_sa?: (string | null | undefined)[] | null | undefined;
  line_ta?: (string | null | undefined)[] | null | undefined;
  line_na?: (string | null | undefined)[] | null | undefined;
  line_ha?: (string | null | undefined)[] | null | undefined;
  line_ma?: (string | null | undefined)[] | null | undefined;
  line_ya?: (string | null | undefined)[] | null | undefined;
  line_ra?: (string | null | undefined)[] | null | undefined;
  line_wa?: (string | null | undefined)[] | null | undefined;
  line_other?: (string | null | undefined)[] | null | undefined;
};

type ZipAddressName = {
  __typename?: "ZipAddressName" | undefined;
  pref_name: string;
  city_name: string;
  city_kana: string;
  section_name: string;
  section_kana: string;
  block_name?: string | null | undefined;
};

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestAddressState = () => {
  const btnNonMemberToggle = ref(0);
  const errorZipCode = ref("");
  const userErrorMessage = ref("");
  const errorPrefSelect = ref("");
  const zipCodeFromPref = ref("");
  const prefListItems = ref<Array<string | null | undefined> | null | undefined>(undefined);
  const pref_name = ref("");
  const pref_name_befor = ref("");
  const listDeliveryArea = ref<
    | Array<{ __typename?: "DeliveryArea" | undefined; area_code: string; area_name: string; acceptable_end_time: string } | null | undefined>
    | null
    | undefined
  >(null);
  const deliveryAreaName = ref("");
  const blockList = ref<Array<string | null | undefined> | null | undefined>([]);
  const zipAddressList = ref<Array<ZipAddressName | null | undefined> | null | undefined>();
  const sectionBlockList = ref<KanaStepA | null | undefined>(null);
  const addressBlockList = ref<Array<string | null | undefined> | null | undefined>([]);
  const unassignedBlockList = ref<Array<string | null | undefined>>([]);
  const zip_code = ref("");
  const prefSelectValue = ref("");
  const cityBlockList = ref<KanaStepA | null | undefined>(null);
  const originalAddress = ref("");
  const originalCityAddress = ref("");
  const originalSectionAddress = ref("");
  const originalBlockName = ref("");
  const addressResolveLoading = ref(false);
  const clickMode = ref(false);

  return {
    btnNonMemberToggle,
    errorZipCode,
    userErrorMessage,
    errorPrefSelect,
    zipCodeFromPref,
    prefListItems,
    pref_name,
    pref_name_befor,
    listDeliveryArea,
    deliveryAreaName,
    blockList,
    zipAddressList,
    sectionBlockList,
    addressBlockList,
    unassignedBlockList,
    zip_code,
    prefSelectValue,
    cityBlockList,
    originalAddress,
    originalCityAddress,
    originalSectionAddress,
    originalBlockName,
    addressResolveLoading,
    clickMode,
  };
};

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliveryRequestAddressParams {
  btnNonMemberToggle: Ref<number>;
  errorZipCode: Ref<string>;
  userErrorMessage: Ref<string>;
  errorPrefSelect: Ref<string>;
  zipCodeFromPref: Ref<string>;
  zip_code: Ref<string>;
  prefSelectValue: Ref<string>;
  pref_name: Ref<string>;
  pref_name_befor: Ref<string>;
  prefListItems: Ref<Array<string | null | undefined> | null | undefined>;
  originalAddress: Ref<string>;
  originalCityAddress: Ref<string>;
  originalSectionAddress: Ref<string>;
  originalBlockName: Ref<string>;
  addressResolveLoading: Ref<boolean>;
  clickMode: Ref<boolean>;
  cityBlockList: Ref<KanaStepA | null | undefined>;
  sectionBlockList: Ref<KanaStepA | null | undefined>;
  blockList: Ref<Array<string | null | undefined> | null | undefined>;
  addressBlockList: Ref<Array<string | null | undefined> | null | undefined>;
  unassignedBlockList: Ref<Array<string | null | undefined>>;
  zipAddressList: Ref<Array<ZipAddressName | null | undefined> | null | undefined>;
  addressDialog: Ref<boolean>;
  areaDialog: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  editDeliveryRequest: Ref<DeliveryRequestUpdateInput | null>;
  listDeliveryArea: Ref<
    | Array<{ __typename?: "DeliveryArea" | undefined; area_code: string; area_name: string; acceptable_end_time: string } | null | undefined>
    | null
    | undefined
  >;
  targetShopCode: Ref<string>;
  deliveryAreaName: Ref<string>;
  acceptableEndTime: Ref<string>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  disp_deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  deliveryId_0_Flg: Ref<boolean>;
  deliveryStartHourItem: Ref<number[]>;
  deliveryStartHourValues: Ref<number | null | undefined>;
  deliveryStartMinitValues: Ref<
    { label: string; deliveryStartMinit: number } | null | undefined
  >;
  displayDeliveryTime: Ref<string>;
  onClickListDeliveryArea: (item: { area_code: string; area_name: string; acceptable_end_time: string }) => Promise<void>;
  getDeliveryTimeList: (chain_code: string) => Promise<void>;
}

const createKanaStepA = (): KanaStepA => ({
  line_a: [],
  line_ka: [],
  line_sa: [],
  line_ta: [],
  line_na: [],
  line_ha: [],
  line_ma: [],
  line_ya: [],
  line_ra: [],
  line_wa: [],
  line_other: [],
});

const resolveSelectedAddressBase = (
  prefSelectValue: string,
  prefName: string,
  city: string,
  section: string
): string => {
  // 住所変更後は現在の選択値から都度再構築し、旧住所の連結残りを防ぐ。
  const pref = prefSelectValue || prefName;
  if (!pref && !city && !section) {
    return "";
  }
  return pref + city + section;
};

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestAddress = ({
  btnNonMemberToggle,
  errorZipCode,
  userErrorMessage,
  errorPrefSelect,
  zipCodeFromPref,
  zip_code,
  prefSelectValue,
  pref_name,
  pref_name_befor,
  prefListItems,
  originalAddress,
  originalCityAddress,
  originalSectionAddress,
  originalBlockName,
  addressResolveLoading,
  clickMode,
  cityBlockList,
  sectionBlockList,
  blockList,
  addressBlockList,
  unassignedBlockList,
  zipAddressList,
  addressDialog,
  areaDialog,
  errdialog,
  errMessage,
  editDeliveryRequest,
  listDeliveryArea,
  targetShopCode,
  deliveryAreaName,
  acceptableEndTime,
  deliveryAvailabilityHeader,
  deliveryAvailabilityData,
  disp_deliveryAvailabilityData,
  deliveryId_0_Flg,
  deliveryStartHourItem,
  deliveryStartHourValues,
  deliveryStartMinitValues,
  displayDeliveryTime,
  onClickListDeliveryArea,
  getDeliveryTimeList,
}: UseDeliveryRequestAddressParams) => {
  const resolvedUnassignedBlockList =
    unassignedBlockList ?? ref<Array<string | null | undefined>>([]);

  const getRequiredShopCode = (): string => {
    const shopCode = localStorageGetShopCode();
    if (!shopCode) {
      throw new Error("店舗コードが取得できません。再ログインしてください。");
    }
    return shopCode;
  };

  const areaClear = async (): Promise<void> => {
    sectionBlockList.value = null;
    blockList.value = [];
    resolvedUnassignedBlockList.value = [];
    originalCityAddress.value = "";
    originalSectionAddress.value = "";
  };

  const areaAddressSelectClear = async (): Promise<void> => {
    pref_name.value = "";
    prefSelectValue.value = "";
    cityBlockList.value = null;
    sectionBlockList.value = null;
    blockList.value = [];
    resolvedUnassignedBlockList.value = [];
    addressBlockList.value = [];
    originalCityAddress.value = "";
    originalSectionAddress.value = "";
    originalBlockName.value = "";
  };

  const onClickAddressUpdate = async (): Promise<void> => {
    pref_name_befor.value = pref_name.value;
    await areaAddressSelectClear();
    const shopCode = getRequiredShopCode();

    const apolloClient = await generateApolloClient();
    const prefList: ApolloQueryResult<GetPrefByChainQuery> =
      await apolloClient.query<GetPrefByChainQuery>({
        query: GetPrefByChainDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: shopCode,
        },
        fetchPolicy: "no-cache",
      });
    prefListItems.value = prefList.data.getPrefByChain;

    // 既存住所がある場合は郵便番号入力モードで開き、入力済みの郵便番号を表示する。
    if (
      editDeliveryRequest.value?.delivery_address1 &&
      editDeliveryRequest.value.delivery_address1 !== "" &&
      editDeliveryRequest.value?.delivery_zip_code &&
      editDeliveryRequest.value.delivery_zip_code !== ""
    ) {
      zip_code.value = editDeliveryRequest.value.delivery_zip_code;
      btnNonMemberToggle.value = 0;
    }

    addressDialog.value = true;
  };

  // エリア選択ダイアログの戻る分岐を共通化し、画面固有の終了処理のみ指定する。
  const { onClickAreaDialogReturn } = useLegacyAreaDialogNavigation({
    blockList: blockList as Ref<Array<unknown> | null | undefined>,
    sectionBlockList: sectionBlockList as Ref<unknown | null | undefined>,
    cityBlockList: cityBlockList as Ref<unknown | null | undefined>,
    originalCityAddress,
    originalSectionAddress,
    unassignedBlockList: resolvedUnassignedBlockList,
    clearUnassignedOnSectionStep: true,
    onDefaultExit: () => {
      pref_name.value = pref_name_befor.value;
      areaDialog.value = false;
      addressDialog.value = true;
    },
  });

  const onClickAddressDialogReturn = async (): Promise<void> => {
    addressDialog.value = false;
    pref_name.value = pref_name_befor.value;
  };

  const getAreaInfo = async (chain_code: string): Promise<void> => {
    const apolloClient = await generateApolloClient();
    let area_data: ApolloQueryResult<GetDeliveryAreaQuery> | null;
    let acceptableEndTimeAry: string[] = [];

    if (!listDeliveryArea.value || listDeliveryArea.value.length === 0) {
      return;
    }

    try {
      for (let i = 0; i < listDeliveryArea.value.length; i++) {
        const shopCode =
          targetShopCode.value !== ""
            ? targetShopCode.value
            : getRequiredShopCode();

        // 個別 area_code の取得を try/catch で包み、取得できなかったエリアはスキップする。
        try {
          area_data = await apolloClient.query<GetDeliveryAreaQuery>({
            query: GetDeliveryAreaDocument,
            variables: {
              chain_code: chain_code,
              customer_code: localStorageGetCustomerCode(),
              shop_code: shopCode,
              area_code: listDeliveryArea.value[i]?.area_code,
            },
            fetchPolicy: "no-cache",
          });

          const deliveryArea = area_data.data.getDeliveryArea;
          if (deliveryArea && deliveryArea.acceptable_end_time) {
            acceptableEndTimeAry = deliveryArea.acceptable_end_time.split(":");
            if (acceptableEndTimeAry.length >= 2 && listDeliveryArea.value[i]) {
              listDeliveryArea.value[i]!.acceptable_end_time =
                acceptableEndTimeAry[0].padStart(2, "0") +
                ":" +
                acceptableEndTimeAry[1].padStart(2, "0");
            }
          }
          if (listDeliveryArea.value[i]?.area_code === editDeliveryRequest.value?.area_code) {
            if (deliveryArea) {
              deliveryAreaName.value = deliveryArea.area_name;
            }
            if (listDeliveryArea.value[i]) {
              acceptableEndTime.value = listDeliveryArea.value[i]!.acceptable_end_time;
            }
          }
        } catch {
          // 一覧情報で継続する
        }
        area_data = null;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        const originalMessage = err.message;
        err.message = `配達エリアの取得に失敗しました。(${originalMessage})`;
        throw err;
      }
      throw new Error("配達エリアの取得に失敗しました。");
    }

    await getDeliveryTimeList(chain_code);
  };

  const getAreaList = async (
    item?: string,
    shop_code?: string,
    area_code?: string,
    chain_code?: string
  ): Promise<void> => {
    if (item == "その他") {
      item = "";
    }
    if (chain_code === undefined) {
      chain_code = localStorageGetChainCode();
    }

    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.value = [];
    disp_deliveryAvailabilityData.value = [];
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;
    displayDeliveryTime.value = "";
    listDeliveryArea.value = null;
    originalBlockName.value = item ? item : originalBlockName.value;
    // 非表示時のフィルタ用に、リセット前の area_code を保持する。
    const originalAreaCode = editDeliveryRequest.value?.area_code || area_code;
    if (editDeliveryRequest.value) {
      editDeliveryRequest.value.area_code = area_code ? area_code : "";
    }

    const selectedAddressBase = resolveSelectedAddressBase(
      prefSelectValue.value,
      pref_name.value,
      originalCityAddress.value,
      originalSectionAddress.value
    );
    if (selectedAddressBase !== "") {
      originalAddress.value = selectedAddressBase;
    } else if (originalAddress.value === "" || originalAddress.value == prefSelectValue.value) {
      originalAddress.value = prefSelectValue.value;
    }

    let address: string | null = null;
    if (originalAddress.value !== "(非表示)") {
      address = originalAddress.value + originalBlockName.value;
    }

    const apolloClient = await generateApolloClient();
    try {
      const resolvedShopCode = shop_code ? shop_code : getRequiredShopCode();
      const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
        await apolloClient.query<
          ListDeliveryAreaQuery,
          ListDeliveryAreaQueryVariables
        >({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: chain_code,
            customer_code: localStorageGetCustomerCode(),
            shop_code: resolvedShopCode,
            address: address,
          },
        });
      listDeliveryArea.value = listArea.data.listDeliveryArea
        ? JSON.parse(JSON.stringify(listArea.data.listDeliveryArea))
        : null;

      // 個人情報非表示時（address=null）は全エリアが返るため、
      // 保存済み area_code で絞り込み、後続処理に対応しないエリアが渡らないようにする。
      if (address === null && listDeliveryArea.value && listDeliveryArea.value.length > 0) {
        if (originalAreaCode) {
          const filtered = listDeliveryArea.value.filter(
            (area) => area?.area_code === originalAreaCode
          );
          if (filtered.length > 0) {
            listDeliveryArea.value = filtered;
          }
        }
      }

      if (editDeliveryRequest.value) {
        editDeliveryRequest.value.delivery_address1 =
          originalAddress.value + originalBlockName.value;
      }
      await getAreaInfo(chain_code);
      if (
        listDeliveryArea.value !== undefined &&
        listDeliveryArea.value !== null &&
        listDeliveryArea.value.length == 1
      ) {
        if (editDeliveryRequest.value) {
          editDeliveryRequest.value.area_code = listDeliveryArea.value[0]!.area_code;
        }
        await onClickListDeliveryArea(listDeliveryArea.value[0]!);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        const originalMessage = err.message;
        err.message = `配達エリア一覧の取得に失敗しました。(${originalMessage})`;
        throw err;
      }
      throw new Error("配達エリア一覧の取得に失敗しました。");
    }
  };

  const setDeliveryAreaSectionUnassigned = async (zipCode: string): Promise<void> => {
    if (
      !blockList.value?.some((block) => block !== null && block !== undefined && block !== "")
    ) {
      return;
    }

    try {
      const apolloClient = await generateApolloClient();
      const shopCode = getRequiredShopCode();
      const prefName = prefSelectValue.value || pref_name.value;
      const selectedAddress =
        prefName + originalCityAddress.value + originalSectionAddress.value;

      const result: ApolloQueryResult<ListDeliveryAreaSectionUnassignedQuery> =
        await apolloClient.query<ListDeliveryAreaSectionUnassignedQuery>({
          query: ListDeliveryAreaSectionUnassignedDocument,
          variables: {
            input: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: shopCode,
              zip_code: zipCode,
            },
          },
          fetchPolicy: "no-cache",
        });

      const unassignedBlocks =
        result.data.listDeliveryAreaSectionUnassigned?.map((block) => {
          return block?.delivery_area_section?.replace(selectedAddress, "") ?? "";
        }) ?? [];

      if (unassignedBlocks.length === 0) {
        return;
      }

      resolvedUnassignedBlockList.value = unassignedBlocks;
      blockList.value = (blockList.value ?? []).concat(unassignedBlocks);
    } catch (err: unknown) {
      if (err instanceof Error) {
        err.message = "丁目の取得に失敗しました。";
        throw err;
      }
      throw new Error("丁目の取得に失敗しました。");
    }
  };

  const onClickToggleZip = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    btnNonMemberToggle.value = 0;
  };

  const onClickTogglePref = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    btnNonMemberToggle.value = 1;
  };

  const onPrefSelectInput = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
  };

  const onZipCodeInput = async (): Promise<void> => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";

    if (zip_code.value.length !== 7) {
      return;
    }

    try {
      const apolloClient = await generateApolloClient();
      const shopCode = getRequiredShopCode();
      const adddressBlock: ApolloQueryResult<GetBlockByZipCodeQuery> =
        await apolloClient.query<GetBlockByZipCodeQuery>({
          query: GetBlockByZipCodeDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: shopCode,
            zip_code: zip_code.value,
          },
          fetchPolicy: "no-cache",
        });

      if (
        !adddressBlock.data.getBlockByZipCode ||
        adddressBlock.data.getBlockByZipCode.length === 0
      ) {
        errorZipCode.value = "入力した郵便番号はご利用対象外です";
      }
    } catch (err) {
      console.error(err);
      userErrorMessage.value = "郵便番号の確認に失敗しました。";
    }
  };

  const initializeAddress = async (): Promise<void> => {
    originalAddress.value = pref_name.value;
    const shopCode = getRequiredShopCode();

    if (pref_name.value !== "") {
      cityBlockList.value = await getCityByPrefGroupedByStepA(
        localStorageGetCustomerCode(),
        localStorageGetChainCode(),
        shopCode,
        pref_name.value
      );
      btnNonMemberToggle.value = 1;
    }

    if (zip_code.value !== "") {
      btnNonMemberToggle.value = 0;
      try {
        originalAddress.value = await getPrefCitySectionByZipCode(
          localStorageGetCustomerCode(),
          localStorageGetChainCode(),
          shopCode,
          zip_code.value
        );
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "住所の取得に失敗しました。";
          throw err;
        }
        throw new Error("住所の取得に失敗しました。（Unknown error）");
      }

      try {
        const apolloClient = await generateApolloClient();
        const adddressBlock: ApolloQueryResult<GetBlockByZipCodeQuery> =
          await apolloClient.query<GetBlockByZipCodeQuery>({
            query: GetBlockByZipCodeDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: shopCode,
              zip_code: zip_code.value,
            },
            fetchPolicy: "no-cache",
          });

        cityBlockList.value = null;
        zipAddressList.value = adddressBlock.data.getBlockByZipCode;
        zipCodeFromPref.value = zip_code.value;

          if (!zipAddressList.value || zipAddressList.value.length === 0) {
            // 配達可能な住所がない場合は住所選択ダイアログにエラーを表示する
            errorZipCode.value = "入力した郵便番号はご利用対象外です";
            return;
        }

        if (zipAddressList.value.length === 1) {
          const first = zipAddressList.value[0];
          if (first) {
            pref_name.value = first.pref_name;
            originalCityAddress.value = first.city_name.replace("　", "");
            originalSectionAddress.value = first.section_name.replace("　", "");
            originalBlockName.value = first.block_name ? first.block_name.replace("　", "") : "";
            await getAreaList();
          }
          areaDialog.value = false;
          return;
        }

        let prev_city_name = "";
        let prev_section_name = "";
        let city_fixed = true;
        let section_fixed = true;
        for (const zipAddress of zipAddressList.value) {
          if (!zipAddress?.section_name) continue;

          if (prev_city_name === "") {
            prev_city_name = zipAddress.city_name;
          } else if (prev_city_name !== zipAddress.city_name) {
            city_fixed = false;
          }

          if (prev_section_name === "") {
            pref_name.value = zipAddress.pref_name;
            prev_section_name = zipAddress.section_name;
          } else if (prev_section_name !== zipAddress.section_name) {
            section_fixed = false;
          }
        }

        if (city_fixed && section_fixed) {
          blockList.value = [];
          resolvedUnassignedBlockList.value = [];
          for (const zipAddress of zipAddressList.value) {
            if (zipAddress) blockList.value.push(zipAddress.block_name);
          }
          const first = zipAddressList.value[0];
          if (first) {
            prefSelectValue.value = first.pref_name;
            originalCityAddress.value = first.city_name.replace("　", "");
            originalSectionAddress.value = first.section_name.replace("　", "");
          }
          await setDeliveryAreaSectionUnassigned(zip_code.value);
          return;
        }

        if (city_fixed) {
          sectionBlockList.value = createKanaStepA();
          const first = zipAddressList.value[0];
          if (first) originalCityAddress.value = first.city_name.replace("　", "");

          let prev_name = "";
          for (const zipAddress of zipAddressList.value) {
            if (!zipAddress) continue;
            const kana_step_a = zipAddress.section_kana.substring(0, 1);
            if (prev_name === zipAddress.section_name) continue;
            prev_name = zipAddress.section_name;

            if (["ｱ", "ｲ", "ｳ", "ｴ", "ｵ"].includes(kana_step_a)) {
              sectionBlockList.value.line_a?.push(zipAddress.section_name.replace("　", ""));
            } else if (["ｶ", "ｷ", "ｸ", "ｹ", "ｺ"].includes(kana_step_a)) {
              sectionBlockList.value.line_ka?.push(zipAddress.section_name.replace("　", ""));
            } else if (["ｻ", "ｼ", "ｽ", "ｾ", "ｿ"].includes(kana_step_a)) {
              sectionBlockList.value.line_sa?.push(zipAddress.section_name.replace("　", ""));
            } else if (["ﾀ", "ﾁ", "ﾂ", "ﾃ", "ﾄ"].includes(kana_step_a)) {
              sectionBlockList.value.line_ta?.push(zipAddress.section_name.replace("　", ""));
            } else if (["ﾅ", "ﾆ", "ﾇ", "ﾈ", "ﾉ"].includes(kana_step_a)) {
              sectionBlockList.value.line_na?.push(zipAddress.section_name.replace("　", ""));
            } else if (["ﾊ", "ﾋ", "ﾌ", "ﾍ", "ﾎ"].includes(kana_step_a)) {
              sectionBlockList.value.line_ha?.push(zipAddress.section_name.replace("　", ""));
            } else if (["ﾏ", "ﾐ", "ﾑ", "ﾒ", "ﾓ"].includes(kana_step_a)) {
              sectionBlockList.value.line_ma?.push(zipAddress.section_name.replace("　", ""));
            } else if (["ﾔ", "ﾕ", "ﾖ"].includes(kana_step_a)) {
              sectionBlockList.value.line_ya?.push(zipAddress.section_name.replace("　", ""));
            } else if (["ﾗ", "ﾘ", "ﾙ", "ﾚ", "ﾛ"].includes(kana_step_a)) {
              sectionBlockList.value.line_ra?.push(zipAddress.section_name.replace("　", ""));
            } else if (kana_step_a === "ﾜ") {
              sectionBlockList.value.line_wa?.push(zipAddress.section_name.replace("　", ""));
            }
          }
          return;
        }

        cityBlockList.value = createKanaStepA();
        let prev_name = "";
        for (const zipAddress of zipAddressList.value) {
          if (!zipAddress) continue;
          const kana_step_a = zipAddress.city_kana.substring(0, 1);
          if (prev_name === zipAddress.city_name) continue;
          prev_name = zipAddress.city_name;

          if (["ｱ", "ｲ", "ｳ", "ｴ", "ｵ"].includes(kana_step_a)) {
            cityBlockList.value.line_a?.push(zipAddress.city_name.replace("　", ""));
          } else if (["ｶ", "ｷ", "ｸ", "ｹ", "ｺ"].includes(kana_step_a)) {
            cityBlockList.value.line_ka?.push(zipAddress.city_name.replace("　", ""));
          } else if (["ｻ", "ｼ", "ｽ", "ｾ", "ｿ"].includes(kana_step_a)) {
            cityBlockList.value.line_sa?.push(zipAddress.city_name.replace("　", ""));
          } else if (["ﾀ", "ﾁ", "ﾂ", "ﾃ", "ﾄ"].includes(kana_step_a)) {
            cityBlockList.value.line_ta?.push(zipAddress.city_name.replace("　", ""));
          } else if (["ﾅ", "ﾆ", "ﾇ", "ﾈ", "ﾉ"].includes(kana_step_a)) {
            cityBlockList.value.line_na?.push(zipAddress.city_name.replace("　", ""));
          } else if (["ﾊ", "ﾋ", "ﾌ", "ﾍ", "ﾎ"].includes(kana_step_a)) {
            cityBlockList.value.line_ha?.push(zipAddress.city_name.replace("　", ""));
          } else if (["ﾏ", "ﾐ", "ﾑ", "ﾒ", "ﾓ"].includes(kana_step_a)) {
            cityBlockList.value.line_ma?.push(zipAddress.city_name.replace("　", ""));
          } else if (["ﾔ", "ﾕ", "ﾖ"].includes(kana_step_a)) {
            cityBlockList.value.line_ya?.push(zipAddress.city_name.replace("　", ""));
          } else if (["ﾗ", "ﾘ", "ﾙ", "ﾚ", "ﾛ"].includes(kana_step_a)) {
            cityBlockList.value.line_ra?.push(zipAddress.city_name.replace("　", ""));
          } else if (kana_step_a === "ﾜ") {
            cityBlockList.value.line_wa?.push(zipAddress.city_name.replace("　", ""));
          }
        }
        areaDialog.value = true;
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "丁目の取得に失敗しました。";
          throw err;
        }
        throw new Error("丁目の取得に失敗しました。（Unknown error）");
      }
    }
  };

  const onClickAddressChangeBtn = async (): Promise<void> => {
    if (addressResolveLoading.value) {
      return;
    }

    if (btnNonMemberToggle.value === 0) {
      // 検索押下時に対象外判定を再実行し、エラー中の画面遷移を防ぐ。
      await onZipCodeInput();
      if (errorZipCode.value) {
        return;
      }
    }

    if (btnNonMemberToggle.value === 1) {
      pref_name.value = prefSelectValue.value;
      zip_code.value = "";
    } else {
      zipCodeFromPref.value = zip_code.value;
      prefSelectValue.value = "";
    }

    addressResolveLoading.value = true;
    try {
      await initializeAddress();

      // 郵便番号検索でエラーが発生した場合は住所選択ダイアログに留まる
      if (errorZipCode.value) {
        return;
      }

      areaDialog.value = true;
      addressDialog.value = false;
    } finally {
      addressResolveLoading.value = false;
    }
  };

  const onClickCityBlockLink = async (item: string): Promise<void> => {
    if (clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      const shopCode = getRequiredShopCode();
      sectionBlockList.value = await getSectionByPrefCityGroupedByStepA(
        localStorageGetCustomerCode(),
        localStorageGetChainCode(),
        shopCode,
        prefSelectValue.value,
        item
      );
      originalCityAddress.value = item;
      blockList.value = [];
      // 市区町村再選択時は前回の配達対象外丁目情報を破棄する。
      resolvedUnassignedBlockList.value = [];
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = "町村の取得に失敗しました。";
      await areaClear();
    } finally {
      clickMode.value = false;
    }
  };

  const onClickSectionBlockLink = async (item: string): Promise<void> => {
    if (clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      const apolloClient = await generateApolloClient();
      const shopCode = getRequiredShopCode();
      if (item === "その他") {
        item = "";
      }

      try {
        const getZipCode: ApolloQueryResult<GetZipCodeByPrefCitySectionQuery> =
          await apolloClient.query<GetZipCodeByPrefCitySectionQuery>({
            query: GetZipCodeByPrefCitySectionDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: shopCode,
              city: originalCityAddress.value,
              pref: pref_name.value,
              section: item,
            },
            fetchPolicy: "no-cache",
          });
        zipCodeFromPref.value = getZipCode.data.getZipCodeByPrefCitySection ?? "";
        if (editDeliveryRequest.value) {
          editDeliveryRequest.value.delivery_zip_code = zipCodeFromPref.value;
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "該当する郵便番号が見つかりませんでした。";
          throw err;
        }
        throw new Error("該当する郵便番号が見つかりませんでした。");
      }

      try {
        const block: ApolloQueryResult<GetBlockByPrefCitySectionQuery> =
          await apolloClient.query<GetBlockByPrefCitySectionQuery>({
            query: GetBlockByPrefCitySectionDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: shopCode,
              city: originalCityAddress.value,
              pref: pref_name.value,
              section: item,
            },
            fetchPolicy: "no-cache",
          });
        blockList.value = block.data.getBlockByPrefCitySection;
        resolvedUnassignedBlockList.value = [];
        originalSectionAddress.value = item;
        originalAddress.value = "";
        originalBlockName.value = "";
        await setDeliveryAreaSectionUnassigned(zipCodeFromPref.value);
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "丁目の取得に失敗しました。";
          throw err;
        }
        throw new Error("丁目の取得に失敗しました。");
      }

      if (
        (blockList.value?.length ?? 0) <= 0 ||
        ((blockList.value?.length ?? 0) === 1 && blockList.value?.[0] === "")
      ) {
        blockList.value = [];
        if (editDeliveryRequest.value) {
          editDeliveryRequest.value.delivery_address1 =
            prefSelectValue.value + originalCityAddress.value + originalSectionAddress.value;
          editDeliveryRequest.value.delivery_zip_code = zipCodeFromPref.value;
        }

        await getAreaList();
        areaDialog.value = false;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "エリア情報の取得に失敗しました。";
      }
      errdialog.value = true;
      await areaClear();
    } finally {
      clickMode.value = false;
    }
  };

  const onClickAddressBlockLink = async (item: string): Promise<void> => {
    if (clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      await getAreaList(item);
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "エリア情報の取得に失敗しました。";
      }
      errdialog.value = true;
      await areaClear();
      return;
    } finally {
      clickMode.value = false;
      if (areaDialog.value) {
        areaDialog.value = false;
      }
    }
  };

  return {
    areaClear,
    getAreaList,
    onClickAddressUpdate,
    onClickAddressDialogReturn,
    onClickAreaDialogReturn,
    onClickToggleZip,
    onClickTogglePref,
    onZipCodeInput,
    onPrefSelectInput,
    onClickAddressChangeBtn,
    onClickCityBlockLink,
    onClickSectionBlockLink,
    onClickAddressBlockLink,
    setDeliveryAreaSectionUnassigned,
  };
};
